import { Controller } from '@hotwired/stimulus'

/**
 * Галерея промо-баннеров на главной (квиз, турнир, расписание тренера).
 *
 * Листается сама, но только пока видна на экране и пока посетитель с ней не
 * взаимодействует: наведение мышью ставит на паузу, фокус клавиатуры внутри —
 * тоже, а свайп, стрелки или точки останавливают автопрокрутку насовсем —
 * дальше человек листает сам. При prefers-reduced-motion автопрокрутки нет.
 *
 * Слайдов больше, чем помещается в ряд, всего на один, поэтому вместо loop
 * (Swiper требует запас слайдов для клонов) — rewind: с последнего слайда
 * галерея возвращается к первому.
 *
 * @extends {Controller}
 */
export default class PromoGalleryController extends Controller {
  /**
   * Тексты a11y-сообщений кнопок и точек, задержка автопрокрутки в мс.
   */
  static values = {
    prevText: { type: String },
    nextText: { type: String },
    bulletText: { type: String },
    delay: { type: Number, default: 6000 }
  }

  /**
   * `container` — корень Swiper, `controls` — строка с точками и стрелками
   * (скрыта до инициализации), `pagination`, `prevBtn`, `nextBtn`.
   */
  static targets = ['container', 'controls', 'pagination', 'prevBtn', 'nextBtn']

  /** @type {import('swiper').default | null} */
  #swiper = null

  /** Отсекает устаревший асинхронный импорт после Turbo-перехода. */
  #initRunId = 0

  /** @type {IntersectionObserver | null} */
  #observer = null

  /** Галерея на экране (хотя бы наполовину). */
  #visible = false

  /** Фокус клавиатуры внутри галереи. */
  #focused = false

  /** Посетитель сам листал — автопрокрутка больше не включается. */
  #stoppedByUser = false

  /** Остановку делает сам контроллер, а не взаимодействие посетителя. */
  #stoppingInternally = false

  static #swiperCache = null

  connect() {
    this.#initRunId += 1
    this.#init(this.#initRunId)
  }

  disconnect() {
    this.#initRunId += 1
    this.#observer?.disconnect()
    this.#observer = null
    this.#swiper?.destroy(true, true)
    this.#swiper = null
    this.#visible = false
    this.#focused = false
    this.#stoppedByUser = false
  }

  /** focusin на корне контроллера. */
  focusIn() {
    this.#focused = true
    this.#sync()
  }

  /** focusout на корне: пауза снимается, только когда фокус ушёл наружу. */
  focusOut(event) {
    if (this.element.contains(event.relatedTarget)) return
    this.#focused = false
    this.#sync()
  }

  /** click по блоку точек: событий о клике по точке Swiper не шлёт. */
  paginationClick(event) {
    if (event.target.closest('.swiper-pagination-bullet')) this.#stopByUser()
  }

  async #init(runId) {
    this.#setControlsVisible(false)

    try {
      const { Swiper, Navigation, Pagination, A11y, Autoplay } =
        await this.#importSwiper()

      if (!this.element.isConnected || runId !== this.#initRunId) return

      const autoplayAllowed = !window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

      this.#swiper = new Swiper(this.containerTarget, {
        modules: [Navigation, Pagination, A11y, Autoplay],
        speed: 600,
        rewind: true,
        slidesPerView: 1.08,
        spaceBetween: 12,
        grabCursor: true,
        breakpoints: {
          768: { slidesPerView: 2, spaceBetween: 16 },
          1024: { slidesPerView: 2, spaceBetween: 24 }
        },
        autoplay: autoplayAllowed
          ? {
              delay: this.delayValue,
              disableOnInteraction: true,
              pauseOnMouseEnter: true
            }
          : false,
        a11y: {
          prevSlideMessage: this.prevTextValue,
          nextSlideMessage: this.nextTextValue
        },
        navigation: {
          nextEl: this.hasNextBtnTarget ? this.nextBtnTarget : null,
          prevEl: this.hasPrevBtnTarget ? this.prevBtnTarget : null
        },
        pagination: this.hasPaginationTarget
          ? {
              el: this.paginationTarget,
              clickable: true,
              renderBullet: (index, className) =>
                `<button type="button" class="${className}" aria-label="${this.bulletTextValue} ${index + 1}"></button>`
            }
          : false,
        // Взаимодействие фиксируем само по себе, а не только по autoplayStop
        // от disableOnInteraction: если автопрокрутку уже остановил фокус
        // на стрелке или уход блока с экрана, Swiper на клик её не
        // останавливает (не запущена) — и без этого она включилась бы
        // снова, как только фокус уйдёт.
        on: {
          afterInit: () => this.#setControlsVisible(true),
          sliderFirstMove: () => this.#stopByUser(),
          navigationNext: () => this.#stopByUser(),
          navigationPrev: () => this.#stopByUser(),
          autoplayStop: () => {
            if (!this.#stoppingInternally) this.#stoppedByUser = true
          }
        }
      })

      if (autoplayAllowed) this.#observeVisibility()
    } catch (error) {
      if (!this.element.isConnected || runId !== this.#initRunId) return
      this.#setControlsVisible(false)
      console.error('Failed to initialize promo gallery', error)
    }
  }

  /**
   * Автопрокрутка Swiper стартует сразу при init; дальше её включает и
   * выключает только видимость галереи — иначе посетитель долистывает до
   * блока, когда тот уже на середине перехода.
   */
  #observeVisibility() {
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        // isIntersecting остаётся true при любой ненулевой видимости,
        // threshold задаёт только моменты вызова — долю сравниваем сами.
        this.#visible = entry.intersectionRatio >= 0.5
        this.#sync()
      },
      { threshold: 0.5 }
    )
    this.#observer.observe(this.element)
  }

  #stopByUser() {
    this.#stoppedByUser = true
    this.#sync()
  }

  #sync() {
    const autoplay = this.#swiper?.autoplay
    if (!autoplay || !this.#swiper.params.autoplay.enabled) return

    const shouldRun = this.#visible && !this.#focused && !this.#stoppedByUser

    if (shouldRun && !autoplay.running) {
      autoplay.start()
    } else if (!shouldRun && autoplay.running) {
      this.#stoppingInternally = true
      autoplay.stop()
      this.#stoppingInternally = false
    }
  }

  #setControlsVisible(visible) {
    if (this.hasControlsTarget) {
      this.controlsTarget.style.visibility = visible ? '' : 'hidden'
    }
  }

  async #importSwiper() {
    if (!PromoGalleryController.#swiperCache) {
      const [{ default: Swiper }, modules] = await Promise.all([
        import('swiper'),
        // eslint-disable-next-line import/no-unresolved
        import('swiper/modules')
      ])
      PromoGalleryController.#swiperCache = { Swiper, ...modules }
    }
    return PromoGalleryController.#swiperCache
  }
}
